// adminfrontend/src/admin_ui/pages/AdminSuppliersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminPurchasingApi from "../services/adminPurchasingApi";
import "../styles/Admin.css";

function AdminSuppliersPage({ adminEmail, onLogout }) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("active");

  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [limit] = useState(100);
  const [offset] = useState(0);

  const hasFilters = Boolean(q || filterStatus);

  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await adminPurchasingApi.listSuppliers({
        limit,
        offset,
        q: q || undefined,
        status: filterStatus || undefined,
      });

      if (!data || data.ok !== true) throw new Error("suppliers_failed");

      const list = data.suppliers || data.items || data.rows || [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(data.total || list.length || 0));
    } catch (e) {
      console.error("[AdminSuppliersPage] load error:", e);
      setError("Unable to load suppliers. Check login or server.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
      const payload = {
        name: name.trim(),
        email: contactEmail.trim() || null,
        phone: phone.trim() || null,
        status,
      };

      const data = await adminPurchasingApi.createSupplier(payload);
      if (!data || data.ok !== true) throw new Error("create_failed");

      setOkMsg("Supplier created.");
      setName("");
      setContactEmail("");
      setPhone("");
      setStatus("active");
      await load();
    } catch (e) {
      console.error("[AdminSuppliersPage] create error:", e);
      setError("Unable to create supplier. Check inputs/server.");
    } finally {
      setMutating(false);
    }
  }

  function onApplyFilters() {
    setOkMsg("");
    load();
  }

  function onClearFilters() {
    setQ("");
    setFilterStatus("");
    setOkMsg("");
    load();
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Suppliers</h1>
            <p className="ucx-admin-subtitle">Create and manage suppliers for purchasing.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Supplier</div>

          <form className="ucx-admin-form" onSubmit={onCreate}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Supplier name" required />
              </div>
              <div className="ucx-admin-field">
                <label>Email</label>
                <input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@email.com"
                />
              </div>
              <div className="ucx-admin-field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+267..." />
              </div>
            </div>

            <div className="ucx-admin-toolbar">
              <div className="ucx-admin-field ucx-admin-field-inline">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>

              <div className="ucx-admin-form-actions">
                <button type="submit" className="ucx-btn ucx-btn-primary" disabled={mutating || !name.trim()}>
                  {mutating ? "Creating…" : "Create Supplier"}
                </button>
              </div>
            </div>

            {okMsg && <div className="ucx-admin-status-success">{okMsg}</div>}
            {error && <div className="ucx-admin-status-error">{error}</div>}
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>

          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search (name/email/phone)" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>

            <button type="button" className="ucx-btn ucx-btn-primary" onClick={onApplyFilters} disabled={loading}>
              Apply
            </button>
            <button
              type="button"
              className="ucx-btn ucx-btn-outline"
              onClick={onClearFilters}
              disabled={loading && !hasFilters}
            >
              Clear
            </button>

            <span className="ucx-small">Showing {normalizedRows.length} of {total}</span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="ucx-admin-empty">
                    Loading suppliers…
                  </td>
                </tr>
              )}

              {!loading && normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="ucx-admin-empty">
                    No suppliers found.
                  </td>
                </tr>
              )}

              {normalizedRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.name}</td>
                  <td>{r.email || "-"}</td>
                  <td>{r.phone || "-"}</td>
                  <td>{r.status || "-"}</td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminSuppliersPage;
